import { useApolloClient } from '@apollo/react-hooks';
import { Dropdown, IDropdownOption } from '@fluentui/react';
import { ApolloError } from 'apollo-client';
import Select from 'components/controls/Select';
import { FilterSelect } from 'components/controls/Select/components/FilterSelect';
import { useForm } from 'modules/forms';
import { UserPermission } from 'modules/permissions';
import { useState, useEffect } from 'react';
import { StyledControlContainer } from 'shared/styled/control.styled';
import { StyledPanelTitle } from 'shared/styled/panel.stayled';
import { mapToDropdownOptions } from 'shared/utils/mapping.utils';
import { isUndefined } from 'shared/utils/null.utils';
import { GetEnhancements, GetServiceCentres } from 'shared/queries/observation-categorisation.queries';
import {
   IDepot,
   IDepotsLookupData,
   IObservationEnhancements,
   Process,
   Reach
} from 'shared/queries/observation-categorisation.queries.types';
import ConcernLevelToggle from 'components/Shared/ConcernLevelToggle';
import { IssueEntity } from 'shared/types/domain/IssueEntity';
import i18next from 'i18next';

interface IssueCategorisationProps {
   readOnly: boolean;
   editPermission?: UserPermission;
}

const IssueCategorisation = ({ readOnly }: IssueCategorisationProps) => {
   const { formData, updateFields, formErrors } = useForm<IssueEntity>();

   const client = useApolloClient();
   const [loading, setLoading] = useState<boolean>(true);
   const [loadingError, setLoadingError] = useState<ApolloError>();

   const [processes, setProcesses] = useState<Process[]>();
   const [reaches, setReaches] = useState<Reach[]>();
   const [depots, setDepots] = useState<IDepot[]>();

   useEffect(() => {
      loadEnhancements();
      loadServiceCentres();
   }, []);

   const loadEnhancements = async () => {
      setLoading(true);

      try {
         let result = await client.query<IObservationEnhancements>({
            query: GetEnhancements,
            fetchPolicy: 'network-only'
         });

         if (result?.data != null) {
            setProcesses(result?.data.nearMissProcesses);
            setReaches(result?.data.nearMissReaches);
         }
      } catch (e) {
         setLoadingError(e as ApolloError);
      } finally {
         setLoading(false);
      }
   };

   const loadServiceCentres = async () => {
      setLoading(true);

      try {
         let result = await client.query<IDepotsLookupData>({
            query: GetServiceCentres,
            variables: {
               companyId: 1
            },
            fetchPolicy: 'network-only'
         });

         if (result?.data != null) {
            setDepots(result.data.depots);
         }
      } catch (e) {
         setLoadingError(e as ApolloError);
      } finally {
         setLoading(false);
      }
   };

   const onRenderOption = (option?: any) => (
      <div style={{ padding: '4px 0', marginTop: '5px' }}>
         <div style={{ fontWeight: 'bold' }}>{option?.text}</div>
         <div style={{ fontSize: '12px', color: '#666' }}>
            {processes?.find(x => x.processId === option.key)?.description}
         </div>
         <div style={{ fontSize: '11px', color: '#999' }}>
            {processes?.find(x => x.processId === option.key)?.example}
         </div>
      </div>
   );

   return (
      <>
         <StyledPanelTitle>Concern Level</StyledPanelTitle>
         <StyledControlContainer>
            <ConcernLevelToggle
               readOnly={readOnly}
               label={'Level of Concern caused by this Issue'}
               useEnglish={i18next.language === 'gb'}
            />
         </StyledControlContainer>

         <StyledControlContainer>
            <StyledPanelTitle>Location Details</StyledPanelTitle>
         </StyledControlContainer>

         <StyledControlContainer>
            <FilterSelect
               label={'At which Sunbelt Rentals depot did it occur?'}
               required={true}
               selectedKey={formData?.depotPcId}
               errorMessage={formErrors?.depotPcId}
               multiSelect={false}
               onChange={(e, v) =>
                  updateFields({
                     depotPcId: { runValidation: true, value: v?.key }
                  })
               }
               options={mapToDropdownOptions<IDepot, IDropdownOption>(
                  depots,
                  depot => depot.pcId,
                  depot => `${depot.pc} - ${depot.pcName}`
               )}
               optionsError={!isUndefined(loadingError)}
               disabled={readOnly}
               hidePersona={true}
               isLoading={loading}
               useEnglish={true}
            />
         </StyledControlContainer>

         <StyledPanelTitle>Categorisation</StyledPanelTitle>
         <StyledControlContainer>
            <Dropdown
               label={'Issue Process'}
               required
               selectedKey={formData?.processId}
               placeholder='Select an option...'
               errorMessage={formErrors?.processId}
               options={mapToDropdownOptions<Process, IDropdownOption>(
                  processes,
                  status => status.processId,
                  status => status.processName
               )}
               onRenderOption={onRenderOption}
               onChange={(e, v) =>
                  updateFields({
                     processId: { runValidation: true, value: v?.key }
                  })
               }
               styles={{
                  dropdownItem: { alignItems: 'unset', height: 'auto', justifyContent: 'unset' },
                  dropdownItemSelected: { height: 'auto' }
               }}
               disabled={readOnly}
            />
            <Select
               label={'Issue Reach'}
               required
               selectedKey={formData?.reachId}
               errorMessage={formErrors?.reachId}
               multiSelect={false}
               onChange={(e, v) =>
                  updateFields({
                     reachId: { runValidation: true, value: v?.key }
                  })
               }
               options={mapToDropdownOptions<Reach, IDropdownOption>(
                  reaches,
                  status => status.reachId,
                  status => status.reachName
               )}
               optionsError={!isUndefined(loadingError)}
               disabled={readOnly}
               isLoading={loading}
               directionalHintNotFixed={false}
               useEnglish={true}
            />
         </StyledControlContainer>
      </>
   );
};

export default IssueCategorisation;
